export function resolveStockVariant(variants, { optionIndex, optionValue, selectedValues = [], currentVariantId = '' }) {
  const index = Number(optionIndex);
  const current = variants.find((variant) => String(variant.id) === String(currentVariantId));
  const optionCount = Math.max(selectedValues.length, current?.options?.length || 0);
  const wanted = Array.from({ length: optionCount }, (_, option) => selectedValues[option] || current?.options?.[option] || '');
  wanted[index] = optionValue;
  return variants.find((variant) => !variant.available && variant.options?.every((value, option) => !wanted[option] || value === wanted[option]));
}

const KLAVIYO_API_ORIGIN = 'https://a.klaviyo.com';
const KLAVIYO_API_REVISION = '2026-07-15';

export function buildBackInStockPayload(email, variantId) {
  return {
    data: {
      type: 'back-in-stock-subscription',
      attributes: {
        profile: {
          data: {
            type: 'profile',
            attributes: { email },
          },
        },
        channels: ['EMAIL'],
      },
      relationships: {
        variant: {
          data: {
            type: 'catalog-variant',
            id: `$shopify:::$default:::${variantId}`,
          },
        },
      },
    },
  };
}

export function buildMarketingConsentPayload(email, listId) {
  return {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: 'Back in Stock',
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email,
              subscriptions: {
                email: {
                  marketing: { consent: 'SUBSCRIBED' },
                },
              },
            },
          },
        },
      },
      relationships: {
        list: {
          data: { type: 'list', id: listId },
        },
      },
    },
  };
}

function klaviyoClientUrl(path, companyId) {
  return `${KLAVIYO_API_ORIGIN}${path}?company_id=${encodeURIComponent(companyId)}`;
}

async function postKlaviyo(path, companyId, payload, fetchImpl) {
  const response = await fetchImpl(klaviyoClientUrl(path, companyId), {
    method: 'POST',
    headers: {
      Accept: 'application/vnd.api+json',
      'Content-Type': 'application/vnd.api+json',
      revision: KLAVIYO_API_REVISION,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`Klaviyo request failed (${response.status})`);
}

export async function submitStockAlert({
  email,
  variantId,
  companyId,
  marketingConsent = false,
  marketingListId = '',
  fetchImpl = globalThis.fetch,
}) {
  await postKlaviyo(
    '/client/back-in-stock-subscriptions',
    companyId,
    buildBackInStockPayload(email, variantId),
    fetchImpl,
  );

  const result = { stockSubscribed: true, marketingSubscribed: null, marketingError: null };
  if (!marketingConsent) return result;

  try {
    if (!marketingListId) throw new Error('Klaviyo marketing list is not configured');
    await postKlaviyo(
      '/client/subscriptions',
      companyId,
      buildMarketingConsentPayload(email, marketingListId),
      fetchImpl,
    );
    result.marketingSubscribed = true;
  } catch (error) {
    result.marketingSubscribed = false;
    result.marketingError = error;
  }
  return result;
}

class LazyOafStockAlert extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.purchase = this.closest('lazy-oaf-purchase');
    this.refreshReferences();
    document.addEventListener('pointerdown', this.onTriggerIntent, { signal, capture: true });
    document.addEventListener('click', this.onTriggerIntent, { signal, capture: true });
    this.addEventListener('click', this.onClick, { signal });
    this.purchase?.setAttribute('data-stock-alert-ready', '');
  }

  refreshReferences() {
    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('[data-stock-alert-form]');
    try {
      this.variants = JSON.parse(this.querySelector('[data-stock-alert-variants]')?.textContent || '[]');
    } catch {
      this.variants = [];
    }
    const signal = this.abort?.signal;
    if (!signal) return;
    this.form?.addEventListener('submit', this.onSubmit, { signal });
    this.dialog?.addEventListener('close', this.onDialogClose, { signal });
    this.dialog?.addEventListener('click', this.onDialogClick, { signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
    if (this.dialog?.open) this.dialog.close();
    if (this.previousOverflow !== undefined) document.documentElement.style.overflow = this.previousOverflow;
    this.purchase?.removeAttribute('data-stock-alert-ready');
  }

  onTriggerIntent = async (event) => {
    if (event.type === 'click' && event.detail !== 0) return;
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-stock-alert-trigger]');
    if (!(trigger instanceof Element) || !this.purchase?.contains(trigger)) return;
    event.preventDefault();
    event.stopPropagation();
    this.refreshReferences();
    const selector = trigger.closest('volt-option-selector');
    const variant = this.resolveVariant(trigger);
    if (!variant) {
      this.purchase?.message?.('Choose the other product options before requesting a stock alert.');
      return;
    }
    this.trigger = trigger;
    this.selector = selector;
    this.selectorScrollTop = selector?.scroller?.scrollTop || 0;
    this.variant = variant;
    this.updateProduct(variant);
    this.reset();
    await selector?.close?.(false, true);
    this.showDialog();
  };

  onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-stock-alert-back]')) {
      this.reopenSelector = true;
      this.closeDialog();
    } else if (event.target.closest('[data-stock-alert-close], [data-stock-alert-continue]')) {
      this.reopenSelector = false;
      this.closeDialog();
    }
  };

  onDialogClick = (event) => {
    if (event.target !== this.dialog) return;
    const rect = this.dialog.getBoundingClientRect();
    if (event.clientY < rect.top || event.clientX < rect.left || event.clientX > rect.right) this.closeDialog();
  };

  showDialog() {
    if (!this.dialog || this.dialog.open) return;
    this.previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    this.dialog.showModal();
  }

  closeDialog() {
    if (!this.dialog?.open) return;
    this.dialog.close();
  }

  onDialogClose = () => {
    document.documentElement.style.overflow = this.previousOverflow || '';
    this.previousOverflow = undefined;
    if (this.reopenSelector && this.selector?.isConnected) {
      this.selector.open?.({ keyboard: true, preserveScroll: true });
      requestAnimationFrame(() => {
        if (this.selector?.scroller) this.selector.scroller.scrollTop = this.selectorScrollTop;
        this.trigger?.focus?.({ preventScroll: true });
      });
    } else {
      this.selector?.summary?.focus?.({ preventScroll: true });
    }
    this.reopenSelector = false;
  };

  resolveVariant(trigger) {
    const direct = this.variants.find((variant) => String(variant.id) === trigger.dataset.variantId);
    if (direct && !direct.available) return direct;
    const picker = trigger.closest('variant-picker');
    const selectedValues = [...(picker?.querySelectorAll('fieldset') || [])].map((fieldset) => fieldset.querySelector('input:checked')?.value || '');
    return resolveStockVariant(this.variants, {
      optionIndex: trigger.dataset.optionIndex,
      optionValue: trigger.dataset.optionValue,
      selectedValues,
      currentVariantId: this.purchase?.dataset.currentVariantId,
    });
  }

  reset() {
    this.form?.removeAttribute('hidden');
    this.querySelector('[data-stock-alert-success]')?.setAttribute('hidden', '');
    this.form?.reset();
    this.setSubmitting(false);
    this.status('');
    this.successStatus('');
  }

  updateProduct(variant) {
    const image = this.querySelector('[data-stock-alert-product-image]');
    const link = this.querySelector('[data-stock-alert-product-link]');
    const title = this.querySelector('[data-stock-alert-product-title]');
    const price = this.querySelector('[data-stock-alert-product-price]');
    const compare = this.querySelector('[data-stock-alert-product-compare]');
    if (image) {
      image.src = variant.image || '';
      image.alt = variant.title || '';
    }
    if (link) link.href = variant.url || '#';
    if (title) title.textContent = variant.title || '';
    if (price) price.textContent = variant.price || '';
    price?.closest('.lazy-oaf-stock-alert__prices')?.classList.toggle('is-sale', Boolean(variant.compareAtPrice));
    if (compare) {
      compare.textContent = variant.compareAtPrice || '';
      compare.hidden = !variant.compareAtPrice;
    }
  }

  onSubmit = async (event) => {
    event.preventDefault();
    if (this.submitting) return;
    const email = this.querySelector('[data-stock-alert-email]');
    if (!email?.reportValidity()) return;
    if (!this.variant?.id) {
      this.status('Choose a size before requesting a stock alert.');
      return;
    }

    this.status('');
    this.successStatus('');
    this.setSubmitting(true);
    try {
      const result = await submitStockAlert({
        email: email.value.trim(),
        variantId: this.variant.id,
        companyId: this.dataset.klaviyoCompanyId,
        marketingConsent: Boolean(this.querySelector('[data-stock-alert-consent]')?.checked),
        marketingListId: this.dataset.klaviyoMarketingListId,
      });
      this.form.hidden = true;
      const success = this.querySelector('[data-stock-alert-success]');
      success?.removeAttribute('hidden');
      if (result.marketingSubscribed === false) {
        this.successStatus('Your stock alert is set, but we couldn’t add marketing emails. You can try that again from the newsletter sign-up.');
      }
      success?.querySelector('[data-stock-alert-success-heading]')?.focus({ preventScroll: true });
    } catch {
      this.status('We couldn’t set your stock alert. Please try again.');
    } finally {
      this.setSubmitting(false);
    }
  };

  setSubmitting(submitting) {
    this.submitting = submitting;
    const button = this.querySelector('[data-stock-alert-submit]');
    if (!button) return;
    button.disabled = submitting;
    button.toggleAttribute('aria-busy', submitting);
    button.textContent = submitting ? 'SENDING…' : 'EMAIL ME';
  }

  status(message) {
    const status = this.querySelector('[data-stock-alert-status]');
    if (status) status.textContent = message;
  }

  successStatus(message) {
    const status = this.querySelector('[data-stock-alert-success-status]');
    if (status) status.textContent = message;
  }
}

if (!customElements.get('lazy-oaf-stock-alert')) customElements.define('lazy-oaf-stock-alert', LazyOafStockAlert);
